import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import Auth from './components/Auth';
import DraftsPageContent from './components/DraftsPageContent';
import RecordViewer from './components/RecordViewer';
import DraftTimingAnalytics from './pages/DraftTimingAnalytics';
import { DraftProvider } from './contexts/DraftContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LandingPage from './pages/LandingPage';
import MarketingLandingPageV3 from './pages/MarketingLandingPageV3';
import AdminPage from './pages/AdminPage';
// COMMERCIAL-DEMO: public Draft Simulator disabled.
// import DraftSimulator from './pages/DraftSimulator';
import ScoutSimulator from './pages/ScoutSimulator';
import SharedDraftView from './pages/SharedDraftView';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider } from './contexts/AuthContext';
import firebaseService from './services/FirebaseService';

// Create a dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  typography: {
    fontFamily: '"Orbitron", monospace',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 'bold',
        },
      },
    },
  },
});

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #121212;
  color: #fff;
`;

const Header = styled.header`
  background-color: #1a1a1a;
  padding: 16px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const Logo = styled.h1`
  margin: 0;
  font-size: 24px;
  color: #e94560;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100vw;
  overflow-x: auto;
`;

// Admin route component - checks if user is an admin
const AdminRoute = ({ children }) => {
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const user = firebaseService.getCurrentUser();
        if (user) {
          const adminStatus = await firebaseService.isUserAdmin(user.uid);
          setIsAdmin(adminStatus);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error checking admin access:', error);
        setLoading(false);
      }
    };
    
    checkAdminAccess();
  }, []);

  if (loading) {
    return null; // or a loading spinner
  }
  
  if (!isAdmin) {
    return <Navigate to="/drafts" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <DraftProvider>
        <Router>
          <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<LandingPage />} />
              <Route path="/demo" element={<MarketingLandingPageV3 />} />
              <Route path="/shared/:token" element={<SharedDraftView />} />

              {/* COMMERCIAL-DEMO: public Draft Simulator disabled.
              <Route
                path="/simulator"
                element={<DraftSimulator />}
              />
              <Route
                path="/simulator/:draftId"
                element={<DraftSimulator />}
              />
              */}
              <Route path="/simulator" element={<Navigate to="/login" replace />} />
              <Route path="/simulator/:draftId" element={<Navigate to="/login" replace />} />

              {/* Embedded full-feature draft board (no auth, no header).
                  Used by external host apps that gate access on their side
                  and need an editable DraftBoard prefilled via
                  sessionStorage.pendingDraftLoad. Same surface as /drafts
                  minus the header/auth UI. */}
              <Route
                path="/embed"
                element={
                  <MainContent>
                    <DraftsPageContent />
                  </MainContent>
                }
              />

              {/* Protected Routes */}
              <Route element={<PrivateRoute />}>
                {/* Main Dashboard */}
                <Route
                  path="/drafts"
                  element={
                    <AppContainer>
                      <MainContent>
                        <DraftsPageContent />
                      </MainContent>
                    </AppContainer>
                  }
                />

                {/* Record Viewer */}
                <Route
                  path="/records"
                  element={<RecordViewer />}
                />

                {/* Scout Simulator - draft vs a real team using historical pro
                    drafts. Auth-gated. */}
                <Route
                  path="/scout-sim"
                  element={<ScoutSimulator />}
                />

                {/* Draft Timing Analytics */}
                <Route
                  path="/analytics/timing"
                  element={<DraftTimingAnalytics />}
                />

                {/* Admin Page - Additional admin check */}
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <AppContainer>
                        <Header>
                          <Logo>Dota 2 Draft Planner/Simulator</Logo>
                          <Auth />
                        </Header>
                        <MainContent>
                          <AdminPage />
                        </MainContent>
                      </AppContainer>
                    </AdminRoute>
                  }
                />
              </Route>

              {/* Fallback route - redirect all other routes to home page */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ThemeProvider>
        </Router>
      </DraftProvider>
    </AuthProvider>
  );
}

export default App;